import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { parse } from '../../../../lib/query'
import clsx from 'clsx'
import { Box, Checkbox, makeStyles, TextField, Typography } from '@material-ui/core'
import { Controller, useFormContext } from 'react-hook-form'

import { CarLocation } from '../../../../components/book-address'
import { DayChips } from '../../../../components/day-chips'
import { RegLookup } from '../../../../components/reg-lookup'
import { CAR_LOOKUP, carLine, carName, plate, SIZE_SOURCE } from '../../../../lib/vehicle'
import { formVersion } from '../../../../lib/form-version'
import { TimeSlots } from '../../../../components/time-slots'
import { WashPicker, takeCompareReturn } from '../wash-picker'
import { WASH_DESCRIPTORS } from '../../../../lib/wash-copy'
import { carSizeOptions } from '../../../../lib/car-size-copy'
import { ExtrasChips } from '../extras-chips'
import { CheckoutPanel } from '../step-checkout/step-checkout'
import {
  context,
  setAddress,
  setPostcode,
  setDate,
  setTimeSlot,
  setNotes,
  setLargeCar,
  setCongestionZone,
  setAssumed,
  useSelectors,
  useToggleExtra
} from '../../../../store'
import { CONGESTION_ZONE_SERVICE, LARGE_CAR_SERVICE, fields, settings, slots } from '../../../../store/constants'
import { AnalyticsService, BOOKING_STEP_INDEX } from '../../../../services/analytics'
import { isFullPostcode, mayBeInCongestionZone, normalisePostcode } from '../../../../lib/postcode'
import { postcodeCheckResult, postcodeNote, postcodeOutward } from '../../../../lib/postcode-check'
import { addressLookupOn, freeLocation, locationFields, MANUAL_EVENT, PICK_EVENT, pickedLabel, typedLocation } from '../../../../lib/car-location'
import { isValidUkMobile, normaliseUkMobile } from '../../../../lib/mobile'
import { ASSUMED_SIZE_LINE, firstAvailableDay, payDefaults, sizeWasChosen } from '../../../../lib/pay-defaults'
import { dayChips } from '../../../../lib/day-chips'
import { applyServiceSlug, SLUG_TO_SERVICE_NAME } from '../../../../lib/apply-service-slug'

const NAME_TO_SLUG = Object.fromEntries(Object.entries(SLUG_TO_SERVICE_NAME).map(([slug, name]) => [name, slug]))
import { useLivePrice } from '../../../../lib/use-live-price'
import { PriceText } from '../../../../lib/price-text'
import { STEPS } from '../steps'
import { useBookingProgress } from './use-booking-progress'
import { noteProps } from '../../../../lib/booking-progress'
import { clampNotes, NOTES_MAX_LENGTH, notesCounter } from '../../../../lib/notes-limit'
import { navigationType, useBookDraft } from './use-book-draft'
import { useHeroPrefill } from './use-hero-prefill'
import { washPenceForSize } from '../../../../lib/car-price'
import { embeddedCheckout, walletAddressFromSheet } from '../../../../lib/checkout-mode'
import { useDraftCapture } from './use-draft-capture'

import { styles } from './styles'
import { money } from '../../../../lib/gbp'
import { colors } from '../../../../theme/tokens'
import { radioGroupKeyDown } from '../../../../lib/radio-keys'
import { GREEN } from '../../../../components/choice-look'

// Once per module: makeStyles called inside a render built a new JSS sheet,
// with new class names, on every render (booking screen, 11 Oct 2026).
const useStyles = makeStyles(styles)

// The car size's ring, a tick once chosen: a currentColor (--mute) ring,
// then a filled green circle with a white tick, as on the wash cards
// (components/choice-look.js).
const SizeTick = ({ on, className }) => (
  <svg className={className} viewBox="0 0 20 20" aria-hidden="true" focusable="false">
    <circle cx="10" cy="10" r="9" fill={on ? GREEN : 'none'} stroke={on ? GREEN : 'currentColor'} strokeWidth="1.5" />
    {on && <path d="M6 10.5l2.6 2.6L14 7.6" fill="none" stroke={colors.onAccent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
  </svg>
)

// Book & pay — the booking-flow experiment's arm B, on one screen:
//   wash (3 cards, more behind a link) → size → date + slot → extras chips
//   → Apple Pay / Google Pay / Link, or card.
// The car's registration is one box, required on both paths since 20 Oct
// 2026 (components/reg-lookup; a UK plate format, never a lookup).
// Where the car is (18 Oct 2026, amending 5 Oct's "the wallet only pays"):
// with the one embedded checkout a wallet payer types NOTHING here; the
// Apple Pay / Google Pay / Link sheet gives the address, the phone and the
// email (Robin: "yea I think we need to be able to get the details from
// apple or google pay", "fewest boxes and fields is what Im thinking..").
// The card path keeps its typed postcode, house number and mobile, shown in
// the card section once "Or pay by card" is open (`cardFields`). Without the
// embedded checkout (the legacy buttons) all three stay above both buttons.
// A car park, level or bay goes in the notes, on both paths.
//
// FORM V3 (20 Oct 2026, the default; docs/DECISIONS.md). Robin: "imo,
// registration and address should come after base service selection.. and
// then day and time window become a computed function of aggregate operator
// availability in that location", "yea reorder now, intelligence
// incrementally" and "think extras can be a turn off when folks are just
// looking to default to mini valet". So: wash; your car (the Registration
// box; the plate's car sets the size, the two size buttons only when it
// cannot, or after "Not right? Change size"); the car location; the day and
// time for that district (lib/availability.js); "Add extras (optional)",
// folded; the note and the mobile; pay. With the wallet sheet's address the
// location stays the card path's. FORM_VERSION=v2 is the layout before it.
// A DUMB FORM (Robin, 31 Oct 2026): nothing here refuses a pay press. See
// lib/pay-defaults.js for the whole argument and the numbers. What used to
// live in this file and no longer exists anywhere in it:
//
//   payProblems / carLocationProblems / regProblems  the three checks the
//     press ran over the registration, the car location and the mobile
//   the `required` rules on the car size, the day and the time
//   the refusal branch in onBeforePay (setError, field_blocked,
//     validation_blocked, the panel at the buttons, the size hint, the
//     "what is still missing" list and the greying of the pay buttons)
//
// They are DELETED, not switched off, so nobody reintroduces one by editing
// a flag. `AnalyticsService.trackValidationBlocked` and `trackFieldBlocked`
// are still in services/analytics.js, and Stripe's own card errors still
// use them, so the rate can be shown to have reached zero.
//
// What survives, because it is somebody else's data and not our opinion:
// the registration lookup (components/reg-lookup: the plate fills the make,
// model and size) and the address picker (components/book-address). Both are
// autocomplete. Neither gates anything.
const V3 = formVersion() === 'v3'

// The analytics key for each location field. The house number or name is
// still `fields.address` in the form (and `address` on the order), but its
// field_engaged / field_blocked key is `house_number` since 5 Oct 2026, when
// the field stopped being a free "car location" box.
const ANALYTICS_FIELD = { [fields.address]: 'house_number', [fields.carLocation]: 'car_location', [fields.carRegistration]: 'registration' }
const analyticsField = name => ANALYTICS_FIELD[name] || name
// The form's values -> what noteProps reads (notes_text and the rest).
const noteValues = values => ({ notes: values?.[fields.notes], house_number: values?.[fields.address] })

// The one Car location box stands for the postcode and the house number
// (25 Oct 2026, lib/car-location.js): with the address lookup on it lists
// addresses; switched off, or "Enter it manually", it is plain free text.
// `freeLocation` only decides which box is drawn. Nothing here judges what
// is in it: an empty Car location books (31 Oct 2026) and the order is held
// as needing details until Aider has an address.
const isFreeLocation = values => freeLocation(values, addressLookupOn())

// Where a blocking field is on the page: the text inputs by id, the rest by
// the data-book-field marker on their wrapper.
const fieldElement = name => (typeof document === 'undefined'
  ? null
  : document.getElementById(`book-${name}`) || document.querySelector(`[data-book-field="${name}"]`))

// Take the customer to a field they asked to go to ("Enter it manually").
// react-hook-form's setFocus never moved anything here (MUI TextField hands
// the ref to its root div), so this does the scroll and the focus itself.
// It is no longer used by a refusal, because there are none: nothing on this
// screen sends anybody anywhere they did not tap to go.
const showField = name => {
  const el = fieldElement(name)
  if (!el) return
  try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }) } catch (e) { el.scrollIntoView() }
  if (el.tagName === 'INPUT' && el.type !== 'date') {
    try { el.focus({ preventScroll: true }) } catch (e) { /* older browsers */ }
  }
}

// goToField is gone with the unpaid line's "Next: ..." link (19 Oct 2026):
// the line no longer lists fields, because none of them is required, so
// there is no "next thing" to be sent to.

// Has Book & pay been on screen in this page load? A Back to it inside the
// app is then a return, not a fresh link (linkWins below).
let bookMounted = false

export const StepBook = ({ sidebar }) => {
  const classes = useStyles()
  const { dispatch } = useContext(context)
  const history = useHistory()
  const query = parse(history.location.search)
  const bookingForm = useFormContext()
  const { formState } = bookingForm
  // `errors` is react-hook-form's, and nothing on this screen sets one any
  // more (31 Oct 2026): no field carries a rule and the pay press refuses
  // nothing. It is still read by the boxes that paint their own soft notes.
  const { errors } = formState
  const values = bookingForm.getValues()

  const { prices, services, servicesAvailability, baseService, price, config, user } = useSelectors()
  const toggleExtra = useToggleExtra()
  const priceFor = useLivePrice()
  const largeCarPrice = priceFor('Extra for large car')
  // A link that names no wash opens on the three-wash picker with the most
  // booked one ticked (G-216), so the choice is visible, not a "Change" away.
  const [pickerOpen, setPickerOpen] = useState(!query?.service)
  // Back on an unpaid form: the draft put fields back (a reload, Back, a
  // revisit), or the tab came back after a while away. The line at the pay
  // area then says "Welcome back. Next: ..." until the next pay press
  // (lib/unpaid-status.js; replay 01a1497c, 17 Oct 2026).
  const [returned, setReturned] = useState(false)
  // The one embedded checkout carries the unpaid line ("Next: ..."), which
  // replaces the one-line notes at the pay buttons (the legacy buttons keep
  // them).
  const unified = embeddedCheckout()
  // 'wallet' | 'card' (OneCheckout's onPathChange): which path the page is
  // asking for. The wallet needs only taps; the card also the typed fields.
  const [payPath, setPayPath] = useState('wallet')
  const onPathChange = useCallback(path => setPayPath(path), [])
  // WALLET_ADDRESS_FROM_SHEET (off by default, ships dark): the wallet sheet
  // gives the address and the phone. Off: the page as it was.
  const sheet = walletAddressFromSheet()
  // Form v3: the plate's lookup has answered (the size buttons show when it
  // gave no size); "Not right? Change size" ('change') or the unpaid line
  // ('ask') opened the size buttons; "Add extras (optional)" was tapped.
  const [lookupDone, setLookupDone] = useState(false)
  const [sizeOpen, setSizeOpen] = useState(null)
  const [extrasOpen, setExtrasOpen] = useState(false)

  const pricesLoaded = prices.ids.length > 0
  const hasWash = Object.entries(services).some(([id, v]) => v && prices.byId[id]?.group === 'base')
  const currentBaseName = hasWash ? baseService?.name : null
  // What the chosen wash is, in one line: the same words as the wash cards
  // (lib/wash-copy.js). The four-bullet list it replaced cost about 200px on
  // a phone (booking-screen-trim, 9 Oct 2026). A wash with no line shows its
  // name and price only.
  const washLine = hasWash ? WASH_DESCRIPTORS[NAME_TO_SLUG[baseService?.name]] || null : null

  // Mirror the form into the store as the customer goes, so both pay paths
  // read current values (effect after render — not from inside a
  // react-hook-form watch callback, which re-enters RHF mid-event).
  const dispatchDetails = useCallback(data => {
    dispatch(setDate(data[fields.date]))
    dispatch(setTimeSlot(data[fields.timeSlot]))
    dispatch(setNotes(data[fields.notes]))
    // null until a size is picked, so the events can tell 'not yet' from small.
    dispatch(setLargeCar(data[fields.carSize] ? data[fields.carSize] === 'large' : null))
    dispatch(setCongestionZone(!!data[fields.congestionZone]))
    // The address and postcode too: handleCheckout reads them from the store
    // through a closure, so they must already be there before Pay is pressed
    // (a dispatch inside onBeforePay lands one render too late).
    if (data[fields.address]) dispatch(setAddress(data[fields.address]))
    if (data[fields.postcode]) dispatch(setPostcode(normalisePostcode(data[fields.postcode])))
  }, [dispatch])
  const watched = bookingForm.watch()
  const watchedKey = JSON.stringify(watched, (k, v) => (v instanceof Date ? v.toISOString() : v))
  useEffect(() => {
    dispatchDetails(bookingForm.getValues())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedKey])

  // The whole form survives a reload or a return (use-book-draft.js). What it
  // puts back is fill_source 'persisted' in booking_form_exit, not filled.
  // Unfinished bookings (use-draft-capture.js): saved once a valid email or
  // mobile is typed, and put back from the reminder's link before the form
  // restores (resumePending holds the restore back until it is written).
  const { pending: resumePending } = useDraftCapture({ bookingForm })
  const { restored: draftRestored, servicesRestored } = useBookDraft({ bookingForm, pricesLoaded: pricesLoaded && !resumePending, watchedKey, onRestore: name => { bookingProgress.noteSource(name, 'persisted'); setReturned(true) } })

  // Back from "Compare all washes": the picker open again and in view, once
  // the draft is back and after the browser's own scroll restore.
  useEffect(() => {
    if (!draftRestored || !takeCompareReturn()) return undefined
    setPickerOpen(true)
    const t = setTimeout(() => document.querySelector('[data-book-section="wash"]')?.scrollIntoView({ block: 'start' }), 50)
    return () => clearTimeout(t)
  }, [draftRestored])

  // ?service=<slug> from a tile → select it (same rule as the Details step).
  // Once per slug: re-applying it on every change put the tile's wash back the
  // moment a visitor picked another one under "Change" (20 Oct 2026).
  // After the draft is back, never before: both ran in the same commit and the
  // draft's wash landed second, so a visitor who had looked at one wash and
  // then tapped another wash's card got the first one back (25 Oct 2026,
  // e2e/wash-entry.spec.ts). The link names the wash; the draft's extras stay.
  // Except on a reload or a Back to this same link (`linkWins` false): then a
  // draft's wash is the one the visitor last picked, under "Change" perhaps.
  const serviceAppliedRef = useRef(null)
  const linkWins = useRef(history.action !== 'POP' || (!bookMounted && navigationType() === 'navigate')).current
  useEffect(() => { bookMounted = true }, [])
  useEffect(() => {
    if (!pricesLoaded || !draftRestored || !query?.service || serviceAppliedRef.current === query.service) return
    const targetName = SLUG_TO_SERVICE_NAME[query.service]
    if (!targetName) return
    serviceAppliedRef.current = query.service
    if (servicesRestored && !linkWins) return
    if (targetName !== currentBaseName) applyServiceSlug(query.service, { prices, services, dispatch })
  }, [pricesLoaded, draftRestored, servicesRestored, linkWins, currentBaseName, query?.service, prices, services, dispatch])

  // ?extra=<catalogue key> from a before/after "Book this" link
  // (components/before-after): tick that add-on once, after the wash from
  // ?service= (if any) has landed, so the wash change cannot clear it. Once
  // per mount, so a customer who unticks it keeps it unticked; skipped when
  // the chosen wash already includes it.
  const extraAppliedRef = useRef(false)
  useEffect(() => {
    if (extraAppliedRef.current || !pricesLoaded || !draftRestored || !hasWash || !query?.extra) return
    const wantedBase = query.service ? SLUG_TO_SERVICE_NAME[query.service] : null
    if (wantedBase && wantedBase !== currentBaseName) return
    extraAppliedRef.current = true
    const extra = Object.values(prices.byId).find(s => s.key === query.extra && s.group !== 'base')
    if (extra && !services[extra.id] && servicesAvailability[extra.id] !== false) toggleExtra(extra.id)
  }, [pricesLoaded, draftRestored, hasWash, currentBaseName, query?.extra, query?.service, prices, services, servicesAvailability, toggleExtra])

  // From the home page's reg-price fold (?reg=&size=): plate, car and size
  // in, with the same precedence as ?service=: the link beats a stored draft,
  // except on a reload or a Back, when the draft holds what was last chosen.
  useHeroPrefill(bookingForm, query, draftRestored, linkWins, name => bookingProgress.noteSource(name, 'prefill'))

  // The tab back after 30 s or more away with details typed in (Teresa's tab
  // came back 12 minutes later on the same, complete-looking form).
  useEffect(() => {
    if (typeof document === 'undefined') return undefined
    let hiddenAt = null
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return }
      const v = bookingForm.getValues()
      if (hiddenAt && Date.now() - hiddenAt >= 30000 && (v[fields.carLocation] || v[fields.postcode] || v[fields.address] || v[fields.mobile])) setReturned(true)
      hiddenAt = null
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [bookingForm])

  // A signed-in customer's mobile on file starts in the field, only if it is
  // a real UK mobile: Robin's own account showed "12345678" there (9 Oct
  // 2026). A junk one leaves the box empty, and the typed number replaces it
  // on the account at the pay press (step-checkout).
  useEffect(() => {
    if (!user?.mobile || bookingForm.getValues(fields.mobile)) return
    if (isValidUkMobile(user.mobile)) {
      bookingForm.setValue(fields.mobile, normaliseUkMobile(user.mobile))
      bookingProgress.noteSource(fields.mobile, 'prefill')
    } else AnalyticsService.trackMobilePrefillSkipped()
  }, [user?.mobile, bookingForm])

  // What is in the boxes is what counts. A browser autofill that sets an
  // input without an input event (some iOS and password-manager fills)
  // never reaches the form, and the next re-render of the form puts the
  // box back to the form's value: the mobile vanished. The pay press blurs
  // the box the customer was in first, and a blur that re-renders (the
  // Registration box's, since 20 Oct 2026) wiped a silent fill before the
  // press could read it. So the boxes are read into the form whenever focus
  // leaves one, and again at the press. A window capture on blur runs before
  // React 16's own blur handling on the document (focusout comes after
  // blur: too late).
  const syncSilentFills = useCallback(() => {
    if (typeof document === 'undefined') return
    ;[fields.carLocation, fields.postcode, fields.address, fields.carRegistration, fields.mobile].forEach(name => {
      const el = document.getElementById(`book-${name}`)
      if (el && el.value && el.value !== (bookingForm.getValues(name) || '')) bookingForm.setValue(name, el.value)
    })
  }, [bookingForm])
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    window.addEventListener('blur', syncSilentFills, true)
    return () => window.removeEventListener('blur', syncSilentFills, true)
  }, [syncSilentFills])

  // Per-field engagement, once per field per mount (same events as Details).
  const engagedRef = useRef(new Set())
  const engage = name => {
    // Every tap or focus, for booking_form_exit (fields_filled counts a
    // field only once it is engaged on this page view).
    if (name) bookingProgress.noteEngaged(name)
    const fieldName = name && analyticsField(name)
    if (!fieldName || engagedRef.current.has(fieldName)) return
    engagedRef.current.add(fieldName)
    AnalyticsService.trackFieldEngaged('book', fieldName)
  }

  // THE PAY PATH, AND IT NEVER SAYS NO (Robin, 31 Oct 2026; lib/pay-defaults.js).
  //
  // Both pay buttons call this before they start. It used to be the gate:
  // `payProblems` over the registration, the car location and the mobile,
  // react-hook-form's `required` rules on the size, the day and the time, red
  // on the fields, `validation_blocked`, `field_blocked`, `pay_panel_shown`
  // and a return of false. ALL OF THAT IS DELETED. What is left reads the
  // boxes, fills in what the customer did not answer, and returns true.
  //
  // It cannot return false. There is no branch that does. The only thing that
  // can still stop a payment is Stripe, inside its own card fields, which is
  // Stripe's business and not ours.
  const onBeforePay = useCallback(async () => {
    setReturned(false)
    // A silent autofill still in the boxes counts (syncSilentFills above).
    syncSilentFills()
    // A Car location typed with its postcode and not picked still books: the
    // postcode and the rest become the order's postcode and address. An empty
    // one books too, and the server holds the order as needing an address.
    const typed = !bookingForm.getValues(fields.addressPicked) && typedLocation(bookingForm.getValues(fields.carLocation))
    if (typed) {
      bookingForm.setValue(fields.postcode, typed.postcode)
      bookingForm.setValue(fields.address, typed.address)
    }
    // The one thing the press fills in: the slot, which is what the form was
    // already showing (Anytime). THE SIZE AND THE DAY ARE LEFT AS GAPS ON
    // PURPOSE, and lib/pay-defaults.js is where the reason is written down:
    // the server fills them (Order#fill_booking_defaults) because it is the
    // only place that can also STAMP them (orders.car_size_assumed,
    // orders.date_assumed) so Aider knows to ask. A form that filled them in
    // here would send an order indistinguishable from one the customer
    // answered. The total on screen is already the smallest-size price, so
    // nothing the customer presses on changes underneath them.
    payDefaults({ values: bookingForm.getValues(), anytime: slots.allday })
      .forEach(({ name, value }) => bookingForm.setValue(name, value, { shouldDirty: true }))
    // WHICH ANSWERS WERE OURS, into the store once, here, where the payload
    // is about to read them (orders.date_assumed / orders.car_size_assumed,
    // Order#missing_details, Aider). One dispatch at the press and none
    // during the form's life: see `dayIsOurs` for the event this cost.
    dispatch(setAssumed({
      date: dayIsOurs.current,
      carSize: !sizeWasChosen(bookingForm.getValues(), bookingForm.getValues(SIZE_SOURCE))
    }))
    dispatchDetails(bookingForm.getValues())
    AnalyticsService.trackBookingStepCompleted(STEPS.BOOK, BOOKING_STEP_INDEX[STEPS.BOOK], noteProps(noteValues(bookingForm.getValues())))
    return true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingForm, dispatchDetails, syncSilentFills, dispatch])

  // Progress top to bottom: booking_section_viewed per data-book-section, and
  // booking_form_exit with what was filled in when the visitor leaves.
  const bookingProgress = useBookingProgress({
    onBeforePay,
    getFieldValues: () => {
      const v = bookingForm.getValues()
      // The Car location box fills the postcode and the address behind the
      // scenes: only the box the customer used counts.
      return { car_size: v[fields.carSize], date: v[fields.date], time_slot: v[fields.timeSlot], car_location: v[fields.carLocation], mobile: v[fields.mobile], notes: v[fields.notes] }
    }
  })

  const trackedBeforePay = bookingProgress.trackedBeforePay

  // THE CAR LOCATION BOX (19 Oct 2026, lib/car-location.js). The lazy
  // address-pick script lists addresses under it and hands a pick here: it
  // fills the postcode and the address the order carries, and the same
  // checks the postcode box made run on it (postcode_checked, the
  // Congestion Charge tick for a zone district, the soft out-of-area note).
  // Unpicked, the box is the address and its postcode is the order's
  // (25 Oct 2026: one box, no house number box).
  const lookupOn = addressLookupOn()
  const freeBox = isFreeLocation(watched)
  const goManual = useCallback(reason => {
    // What is typed stays in the box, now plain free text.
    bookingForm.setValue(fields.locationManual, true)
    bookingForm.clearErrors(fields.carLocation)
    AnalyticsService.trackCarLocation('manual', { reason })
    setTimeout(() => showField(fields.carLocation), 0)
  }, [bookingForm])
  useEffect(() => {
    if (!lookupOn) return undefined
    const onPick = e => {
      const entry = e.detail && e.detail.entry
      if (!entry) return
      const pc = normalisePostcode(entry.postcode)
      bookingForm.setValue(fields.postcode, pc)
      bookingForm.setValue(fields.address, entry.line)
      bookingForm.setValue(fields.addressPicked, true)
      bookingForm.setValue(fields.carLocation, pickedLabel({ ...entry, postcode: pc }), { shouldDirty: true })
      bookingForm.clearErrors([fields.carLocation, fields.postcode, fields.address])
      AnalyticsService.trackPostcodeChecked(postcodeCheckResult(pc, 200), postcodeOutward(pc))
    }
    const onManual = e => goManual((e.detail && e.detail.reason) || 'link')
    document.addEventListener(PICK_EVENT, onPick)
    document.addEventListener(MANUAL_EVENT, onManual)
    return () => {
      document.removeEventListener(PICK_EVENT, onPick)
      document.removeEventListener(MANUAL_EVENT, onManual)
    }
  }, [lookupOn, bookingForm, goManual])
  // Typing in the box after a pick: the old address no longer counts.
  // Unpicked, the postcode in the box and the box itself are the order's
  // postcode and address, as they type, so the district's days, the
  // Congestion Charge tick and the summary follow the typing (a silent fill
  // is read at the press, onBeforePay).
  const onLocationEdit = () => {
    if (bookingForm.getValues(fields.addressPicked)) bookingForm.setValue(fields.addressPicked, false)
    const { postcode, address } = locationFields(bookingForm.getValues(fields.carLocation))
    bookingForm.setValue(fields.postcode, postcode)
    bookingForm.setValue(fields.address, address)
  }
  // "Enter it manually", typed in and then cleared: the address list comes
  // back.
  const manualTypedRef = useRef(false)
  const locationEmpty = !watched[fields.carLocation]
  useEffect(() => {
    if (!lookupOn || !freeBox) { manualTypedRef.current = false; return }
    if (!locationEmpty) { manualTypedRef.current = true; return }
    if (!manualTypedRef.current) return
    manualTypedRef.current = false
    bookingForm.setValue(fields.locationManual, false)
    bookingForm.setValue(fields.addressPicked, false)
  }, [lookupOn, freeBox, locationEmpty, bookingForm])
  // has_picked_address on the pay events and booking_form_exit.
  const hasPicked = !!watched[fields.addressPicked]
  useEffect(() => { AnalyticsService.setPickedAddress(hasPicked) }, [hasPicked])

  // FORM V3: YOUR CAR. The plate's car (the one kept for this plate) and
  // whether the size is the one it set. The size buttons show once the
  // lookup gave no size, a size was picked, or "Not right? Change size".
  const car = V3 && watched[CAR_LOOKUP]?.reg && watched[CAR_LOOKUP].reg === plate(watched[fields.carRegistration]) ? watched[CAR_LOOKUP] : null
  const autoSized = !!car?.suggested_size && watched[SIZE_SOURCE] === 'lookup'
  const showPicker = !V3 || !!sizeOpen || (!autoSized && (lookupDone || !!watched[fields.carSize]))
  // Robin, 20 Oct 2026: "a large car getting a surprise extra 20 could lead
  // to bouncing, but it might still be better than opting in". Ruled: set it,
  // never as a surprise: the step says what the wash costs for THEIR car
  // ("Mini Valet for your Grey Volvo XC90: £89"), the total moves now, and
  // the wash card keeps its "Large car +£20".
  // The same rule the home page's reg-price fold prices with (lib/car-price.js).
  const largeCents = baseService && watched[fields.carSize] === 'large' && autoSized && washPenceForSize(prices.byId[baseService.id]?.customerPriceCents, prices.byId[LARGE_CAR_SERVICE.id]?.customerPriceCents, 'large')
  // Every lookup's answer: a size from the plate replaces none the customer
  // picked, only an earlier plate's.
  const onAnswer = found => {
    setLookupDone(true)
    const size = found?.suggested_size
    const source = bookingForm.getValues(SIZE_SOURCE)
    if (!size || source === 'manual' || source === 'changed' || (source === 'lookup' && bookingForm.getValues(fields.carSize) === size)) return
    bookingForm.setValue(SIZE_SOURCE, 'lookup')
    bookingForm.setValue(fields.carSize, size, { shouldValidate: true, shouldDirty: true })
    dispatch(setLargeCar(size === 'large'))
    AnalyticsService.trackCarSizeSelected(size, 'lookup', 'lookup')
    if (size === 'large') AnalyticsService.trackLargeAutoApplied()
  }

  // FORM V3: THE DAYS FOR THE CAR'S DISTRICT (lib/availability.js), once a
  // whole postcode is known. No answer: every day as before.
  const district = V3 && isFullPostcode(watched[fields.postcode]) ? postcodeOutward(watched[fields.postcode]) : null
  const [availability, setAvailability] = useState(null)
  useEffect(() => {
    if (!district) return undefined
    let live = true
    fetch(`${settings.host}/api/v2/availability?district=${district}`)
      .then(r => (r.ok ? r.json() : null))
      .then(a => { if (live) setAvailability(a?.days || null) })
      .catch(() => {})
    return () => { live = false }
  }, [district])

  // THE DAY IS DEFAULTED ON SCREEN (Robin, 31 Oct 2026: "date the earliest
  // available"), which supersedes 9 Oct's "no default day". The earliest day
  // the customer could have tapped is picked FOR them, shown as chosen, and
  // flagged as ours (`setAssumed`, which rides the order as
  // orders.date_assumed and is what Aider confirms).
  //
  // Why on screen and not at the press: a day filled in silently at the
  // press means somebody pays without ever seeing which day they bought,
  // and lib/pay-defaults.js has the rest of the argument. Never Today, which
  // carries the same-day surcharge.
  //
  // Only when the form has no day and the draft has had its chance
  // (draftRestored), and once: a customer who clears the day gets it back
  // once, not on every render, and a customer who picks one is never
  // overridden. The flag goes false the moment they choose for themselves.
  // WHOSE DAY IS IT. True while the day on the form is the one we chose; false
  // the moment the customer taps a chip of their own. A ref and not state,
  // and NOT dispatched here: the flags go into the store once, at the press
  // (onBeforePay), because a dispatch on every size or day change put an
  // extra render between `onAnswer`'s setValue and its own
  // `large_auto_applied`, and the event went out with total_pence null (found
  // by e2e/form-v3 "in that order", 31 Oct 2026). The store only needs these
  // two booleans at the moment the payload is built.
  const dayIsOurs = useRef(false)
  const dayDefaulted = useRef(false)
  useEffect(() => {
    if (!draftRestored || dayDefaulted.current) return
    if (values[fields.date]) { dayDefaulted.current = true; return }
    const day = firstAvailableDay(dayChips(new Date(), { todayOpen: false }), availability)
    if (!day) return
    dayDefaulted.current = true
    dayIsOurs.current = true
    bookingForm.setValue(fields.date, day, { shouldDirty: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftRestored, availability])

  // A day the customer picks is theirs, so Aider has nothing to confirm.
  // `engage` already fires field_engaged for the tap.
  const onDayPicked = () => {
    engage(fields.date)
    dayIsOurs.current = false
  }

  // BOTH PAY BUTTONS ARE ALWAYS LIVE (Robin, 31 Oct 2026). `canPay` and
  // `canPayCard` were the booking screen's verdict on itself: the wallet
  // sheet opened only when the taps were done, the card button only when the
  // typed boxes were good too. There is no verdict any more. The wallet sheet
  // opens on the first tap, the card goes on the first press, and whatever is
  // missing is filled in by onBeforePay or chased by Aider afterwards.
  const canPayNow = true

  // THE UNPAID LINE stays (17 Oct 2026, lib/unpaid-status.js; replay
  // 01a1497c): an unpaid screen must never look finished. What it no longer
  // does is list "3 quick details" as if they were required, because none of
  // them is: with nothing missing it reads "All set. Tap Apple Pay to book."
  // and keeps the booking honest about not being paid for yet.
  const unpaid = { missing: [], returned }

  // The size on the order is an assumption when neither the plate nor the
  // customer gave one. Said by the size buttons, quietly, never as an error
  // and never blocking: it is the one place a default moves the price, and a
  // price surprise at the door is what we must not create.
  const assumedSize = !sizeWasChosen(values, watched[SIZE_SOURCE])

  // Your details, stacked, each a one-line box with its label above it
  // (Robin, 9 Oct 2026): the Car location, the note, the congestion tick
  // for a zone district only, mobile.
  //
  // With the one embedded checkout (18 Oct 2026) only the note stays here,
  // for both paths; the Car location, the congestion tick and the mobile
  // are the card path's (`cardFields`, in the card section). A
  // wallet payer types nothing: the sheet gives the address and the phone.
  const showCongestion = mayBeInCongestionZone(watched[fields.postcode]) || !!watched[fields.congestionZone]
  const congestionRow = showCongestion && (
    <Controller
      control={bookingForm.control}
      name={fields.congestionZone}
      render={({ field: { value, onChange, ...field } }) => (
        <label className={classes.inlineCheckRow}>
          <Checkbox {...field} checked={!!value} onChange={e => { onChange(e.target.checked); dispatch(setCongestionZone(e.target.checked)) }} size="small" className={classes.checkboxControl} />
          <span className={classes.inlineCheckText}>
            Inside the Congestion Charge Zone?{CONGESTION_ZONE_SERVICE?.customerPrice ? ` +${CONGESTION_ZONE_SERVICE.customerPrice}` : ''}
          </span>
        </label>
      )}
    />
  )
  // One Car location box in place of the postcode and house number
  // (lib/car-location.js; 25 Oct 2026, also "Enter it manually" and the
  // lookup off): the tick follows the postcode in it for a zone district,
  // and one outside the usual area gets the soft note.
  const postcodeRows = (
    <>
      <div className={classes.detailRow}>
        <label className={classes.fieldLabel} htmlFor={`book-${fields.carLocation}`}>Car location</label>
        <div className={clsx(classes.field, classes.compactField)}>
        <CarLocation
          form={bookingForm}
          name={fields.carLocation}
          free={freeBox}
          note={watched[fields.addressPicked] ? postcodeNote(watched[fields.postcode], 200) : null}
          onFocus={() => engage(fields.carLocation)}
          onEdit={onLocationEdit}
          onManual={() => goManual('link')}
          linkClassName={classes.linkBtn}
        />
        </div>
      </div>
      {congestionRow}
    </>
  )
  // Open, one line that grows, optional: one paid order in five carries a
  // note. The operator always meets the customer, so the prompt asks about a
  // second car instead of make and colour (Robin, 20 Oct 2026).
  const notesRow = (
      <div className={classes.detailRow}>
        <label className={classes.fieldLabel} htmlFor={`book-${fields.notes}`}>Anything we should know?</label>
        <div className={clsx(classes.field, classes.compactField)}>
        <Controller
          name={fields.notes}
          control={bookingForm.control}
          render={({ field }) => (
            // data-ph-unmask: the note is recorded as typed in PostHog
            // replays (docs/DECISIONS.md, 17 Oct 2026).
            // Capped at NOTES_MAX_LENGTH (lib/notes-limit.js), with a
            // counter near the cap.
            <TextField {...field} onChange={e => field.onChange(clampNotes(e.target.value))} id={`book-${fields.notes}`} fullWidth placeholder="Washing a second car too? Tell us here and we'll be in touch." InputProps={{ disableUnderline: true, onFocus: () => engage(fields.notes) }} inputProps={{ 'data-ph-unmask': 'true', maxLength: NOTES_MAX_LENGTH }} helperText={notesCounter(field.value)} multiline minRows={1} maxRows={4} />
          )}
        />
        </div>
      </div>
  )
  const mobileRow = (
      <div className={classes.detailRow}>
        <label className={classes.fieldLabel} htmlFor={`book-${fields.mobile}`}>Mobile</label>
        <div className={clsx(classes.field, classes.compactField)}>
        <Controller
          name={fields.mobile}
          control={bookingForm.control}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <TextField
              {...field}
              inputRef={ref}
              id={`book-${fields.mobile}`}
              fullWidth
              placeholder="07766 554433"
              error={!!error}
              helperText={error?.message || null}
              InputProps={{
                disableUnderline: true,
                autoComplete: 'tel',
                type: 'tel',
                inputMode: 'tel',
                onFocus: () => engage(fields.mobile),
                // "07766 554433" once they leave it. Tidied, never judged
                // (31 Oct 2026): the mobile refused 12 presses over 5 people
                // since 5 Oct, and the box used to go red here on a number
                // that was not a UK mobile. It takes whatever is typed, and
                // Aider asks for a better one if we cannot reach them.
                onBlur: () => {
                  field.onBlur()
                  const tidy = normaliseUkMobile(field.value)
                  if (tidy !== field.value) bookingForm.setValue(fields.mobile, tidy)
                  bookingForm.clearErrors(fields.mobile)
                }
              }}
            />
          )}
        />
        </div>
      </div>
  )
  const regBox = <RegLookup form={bookingForm} classes={classes} payPath={payPath} onFocus={() => engage(fields.carRegistration)} onAnswer={V3 ? onAnswer : undefined} />
  // Form v3: the location and the registration have their own sections
  // above; the details are the note and the mobile.
  const addressField = (
    <div className={classes.addressWrap} data-book-pay-fields={sheet ? undefined : ''} data-book-section="details">
      {!sheet && !V3 && postcodeRows}
      {!V3 && regBox}
      {notesRow}
      {!sheet && mobileRow}
    </div>
  )
  const cardFields = sheet && (
    <div className={classes.addressWrap} data-book-pay-fields>
      {postcodeRows}
      {mobileRow}
    </div>
  )

  // The head is the H1 alone (booking-screen-trim, 9 Oct 2026). The charge
  // sentence was on this screen five times; Robin: "keep the your card is
  // charged stuff after the green button ... but remoe from the pay
  // section". The total is on the phone bar, the desktop summary and the pay
  // button itself.
  const head = (
    <div className={classes.pageHead}>
      <h1 className={classes.pageTitle}>Book your wash</h1>
    </div>
  )

  // The two size buttons (both forms).
  const sizePicker = (
    <Controller
      name={fields.carSize}
      control={bookingForm.control}
      // NO RULE (31 Oct 2026). The size was required and the pay press was
      // refused without it, 19 presses over 9 people since 5 Oct. An
      // unanswered size is the smallest now, and the line below says so.
      render={({ field: { value, ...field } }) => (
        <div>
          <input {...field} value={value || ''} hidden />
          <div className={classes.sizeGroup} data-book-field={fields.carSize} role="radiogroup" aria-label="Car size" onKeyDown={radioGroupKeyDown}>
            {carSizeOptions(largeCarPrice).map(({ value: v, title, desc }) => (
              <button
                type="button"
                key={v}
                role="radio"
                aria-checked={value === v}
                className={clsx(classes.sizeOption, value === v && classes.sizeOptionSelected)}
                onClick={() => {
                  engage(fields.carSize)
                  const source = sizeOpen === 'change' ? 'changed' : 'manual'
                  AnalyticsService.trackCarSizeSelected(v, 'form', source)
                  bookingForm.setValue(SIZE_SOURCE, source)
                  bookingForm.setValue(fields.carSize, v, { shouldValidate: true, shouldDirty: true })
                  dispatch(setLargeCar(v === 'large'))
                }}>
                <SizeTick on={value === v} className={classes.sizeTick} />
                <div className={classes.sizeOptionTitle}>{title}</div>
                <div className={classes.sizeOptionDesc}>{desc}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    />
  )
  // Form v3: "Your car", the Registration box and what the plate told us
  // (step 2); v2: the size buttons alone, the plate in the details.
  const carSection = V3 ? (
    <section className={classes.section} data-book-section="car" data-book-field={showPicker ? undefined : fields.carSize}>
      <Typography component="h2" className={classes.sectionHeading}>Your car</Typography>
      {regBox}
      {largeCents ? (
        <>
          <p className={classes.carPrice} data-testid="car-price">{`${baseService.name} for your ${carName(car)}: ${money(largeCents / 100)}`}</p>
          <p className={classes.addressHint}>Bigger cars take longer to do properly.</p>
        </>
      ) : car && <p className={classes.addressHint} data-testid="reg-car">{carLine(car)}</p>}
      {autoSized && !sizeOpen && <button type="button" className={classes.linkBtn} data-testid="change-size" onClick={() => setSizeOpen('change')}>Not right? Change size</button>}
      {/* Always mounted, so its required rule holds while it is hidden. */}
      <div hidden={!showPicker}>{sizePicker}</div>
      {/* The one line a default earns, because this default moves the price
          (31 Oct 2026). Not an error, not red and nothing blocked: the two
          buttons right above it are how a customer who cares corrects it. */}
      {showPicker && assumedSize && <p className={classes.addressHint} data-testid="assumed-size">{ASSUMED_SIZE_LINE}</p>}
    </section>
  ) : (
    <section className={classes.section} data-book-section="car_size">
      <Typography component="h2" className={classes.sectionHeading}>Car size</Typography>
      {sizePicker}
    </section>
  )
  // Form v3: the wash's three featured extras show as chips once a wash is
  // chosen (Robin, 20 Oct 2026: "I would always keep 3 high priority extras
  // visible"); before that the row folds. An extra already chosen (a "Book
  // this" link, a draft) keeps it open.
  const showExtras = !V3 || hasWash || extrasOpen || Object.keys(services).some(id => services[id] && ['popular', 'extra', 'detailing'].includes(prices.byId[id]?.group))

  const card = (
    <Box className={classes.card}>
      {/* Wash */}
      <section className={classes.section} data-book-field="wash" data-book-section="wash">
        <div className={classes.sectionRow}>
          <Typography component="h2" className={classes.sectionHeading}>Wash</Typography>
          {hasWash && !pickerOpen && (
            <button type="button" className={classes.linkBtn} onClick={() => setPickerOpen(true)}>Change</button>
          )}
        </div>
        {hasWash && !pickerOpen ? (
          <div>
            <div className={classes.washRow} data-testid="wash-chosen">
              <span className={classes.washName}>{baseService.name}</span>
              <span className={classes.washPrice}>{priceFor(baseService.name) || baseService.customerPrice}</span>
            </div>
            {/* The size's charge at the price, never carried over silently
                from an earlier visit or another wash (replay 01a1499a: £95 on
                the card, £115 at pay; 17 Oct 2026). */}
            {values[fields.carSize] === 'large' && largeCarPrice && (
              <p className={classes.washSizeLine} data-testid="wash-size-line">Large car +{largeCarPrice}</p>
            )}
            {washLine && <p className={classes.washStepsParent}>{washLine}</p>}
          </div>
        ) : (
          <WashPicker onPicked={() => setPickerOpen(false)} />
        )}
      </section>

      {carSection}

      {/* Form v3: where the car is, before the day (step 3). */}
      {V3 && !sheet && <section className={classes.section} data-book-section="location">{postcodeRows}</section>}

      {/* When: "Date" and "Time" label themselves (no heading). */}
      <section className={classes.section}>
        {/* The day chips take the full width; the time sits under them. */}
        <div className={classes.dayBlock} data-book-field={fields.date} data-book-section="date">
          {/* No required rule: an unpicked day becomes the earliest one
              operations is open, at the press (lib/pay-defaults.js). */}
          <DayChips form={bookingForm} required={false} onPicked={onDayPicked} availability={availability} />
        </div>
        <div className={classes.field} data-book-field={fields.timeSlot} data-book-section="time_slot">
          <TimeSlots form={bookingForm} required={false} onEngage={engage} todayInDate availability={availability} />
        </div>
      </section>

      {/* Extras; on Form v3 folded behind one row until tapped. */}
      <section className={classes.section} data-book-section="extras">
        {showExtras ? (
          <>
            <Typography component="h2" className={classes.sectionHeading}>Extras <span className={classes.optionalTag}>optional</span></Typography>
            <ExtrasChips />
          </>
        ) : (
          <button type="button" className={classes.linkBtn} data-testid="extras-open" aria-expanded="false" onClick={() => setExtrasOpen(true)}>Add extras (optional)</button>
        )}
      </section>

      {/* Your details, then the pay buttons. The pay facts, the 4.1 and the
          charge sentence that sat above the first required field are gone
          (booking-screen-trim, 9 Oct 2026); one charge line and one trust
          line follow the buttons (step-checkout). */}
      <section className={classes.section} data-testid="book-pay-section">
        <Typography component="h2" className={classes.sectionHeading}>Your details</Typography>
        {user?.email && !/@guest\.gowashmycar\.com$/i.test(user.email) && (
          <p className={classes.signedInLine}>Signed in as {user.email}</p>
        )}
        <CheckoutPanel canPay={canPayNow} canPayCard={canPayNow} cardFields={cardFields} onPathChange={onPathChange} onBeforePay={trackedBeforePay} addressField={addressField} typedMobile={(watched[fields.mobile] || '').trim()} unpaid={unpaid} readMobile={() => (bookingForm.getValues(fields.mobile) || '').trim()} />
        {/* The bottom of the pay buttons: booking_section_viewed 'pay_buttons'
            once it is on screen. No height, so no layout shift. */}
        <div data-book-section="pay_buttons" aria-hidden="true" />
      </section>
    </Box>
  )

  return (
    <form id="booking-step-form" autoComplete="on" onSubmit={e => e.preventDefault()} className={classes.page} onFocusCapture={e => engage(e.target?.name)}>
      <div className={classes.layout}>
        <div className={classes.formCol}>{head}{card}</div>
        {sidebar && <div className={classes.sidebarCol}>{sidebar}</div>}
      </div>
    </form>
  )
}
